import { supabase } from './supabaseClient';
import { serviceRoleClient } from './serviceRoleClient';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../config/env';

export class NotificationService {
  static async getAllUserIds() {
    try {
      const { data, error } = await supabase.rpc('admin_get_all_users_all');
      if (error) throw error;
      return (data || []).map(u => u.id).filter(Boolean);
    } catch (e) {
      console.error('getAllUserIds error:', e);
      return [];
    }
  }

  static STAFF_ROLES = [
    'telecaller',
    'serviceexecutive',
    'serviceadvisor',
    'teamlead',
    'admin',
  ];

  static async getUserIdsByRole(roleName) {
    try {
      const { data, error } = await supabase.rpc('user_management_get_users_by_role', {
        p_role: roleName,
        p_page: 1,
        p_limit: 10000,
      });
      if (error) throw error;
      return (data || []).map(u => u.id).filter(Boolean);
    } catch (e) {
      console.error('getUserIdsByRole error:', e);
      return [];
    }
  }

  static async getUserIdsForTarget(targetRole) {
    try {
      const { data, error } = await serviceRoleClient.rpc('notification_get_install_user_ids', {
        p_target_role: targetRole || 'customer',
      });
      if (!error && Array.isArray(data)) {
        return data.filter(Boolean);
      }
    } catch (e) {
      console.warn('install user ids RPC missing, falling back', e);
    }
    if (targetRole === 'all') {
      return this.getAllUserIds();
    }
    if (targetRole === 'staff') {
      const batches = await Promise.all(
        this.STAFF_ROLES.map((role) => this.getUserIdsByRole(role))
      );
      return [...new Set(batches.flat())];
    }
    return this.getUserIdsByRole(targetRole);
  }

  static async getAudiencePreview(targetRole) {
    try {
      const { data, error } = await serviceRoleClient.rpc('notification_audience_preview', {
        p_target_role: targetRole || 'customer',
      });
      if (error) throw error;
      return {
        dbUsers: Number(data?.db_users || 0),
        appInstalls: Number(data?.app_installs || 0),
      };
    } catch {
      return { dbUsers: 0, appInstalls: 0 };
    }
  }

  static async sendBroadcast({ title, message, category, targetRole, sentBy }) {
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/notification-broadcast-send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          title,
          message,
          category,
          target_role: targetRole,
          sent_by: sentBy || null,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!json?.success) {
        return { success: false, sent: 0, error: json?.error || 'Push send fail hua' };
      }
      const preview = await this.getAudiencePreview(targetRole);
      return {
        success: true,
        sent: json.app_installs || json.sent || 0,
        pushOk: json.push_ok || 0,
        pushFail: json.push_fail || 0,
        fcmTokens: json.fcm_tokens || 0,
        expoTokens: json.expo_tokens || 0,
        dbUsers: preview.dbUsers,
        appInstalls: json.app_installs || preview.appInstalls,
        broadcastId: json.broadcast_id || null,
      };
    } catch (e) {
      console.error('sendBroadcast error:', e);
      return { success: false, sent: 0, error: e.message };
    }
  }

  static async getBroadcastHistory(limit = 50, offset = 0) {
    try {
      const { data, error } = await serviceRoleClient.rpc('notification_broadcast_tracking', {
        p_limit: limit,
      });
      if (!error && Array.isArray(data) && data.length) {
        return data;
      }
    } catch (e) {
      console.warn('tracking RPC missing, using basic history', e);
    }
    try {
      const { data, error } = await supabase.rpc('notification_list_broadcasts', {
        p_limit: limit,
        p_offset: offset,
      });
      if (error) throw error;
      return data || [];
    } catch (e) {
      console.error('getBroadcastHistory error:', e);
      return [];
    }
  }

  static async listTemplates(category, targetRole) {
    try {
      const { data, error } = await supabase.rpc('notification_template_list', {
        p_category: category || null,
        p_target_role: targetRole || null,
      });
      if (error) throw error;
      return data || [];
    } catch (e) {
      console.error('listTemplates error:', e);
      return [];
    }
  }

  static async upsertTemplate({ id, title, message, category, targetRole }) {
    try {
      const { data, error } = await supabase.rpc('notification_template_upsert', {
        p_title: title,
        p_message: message,
        p_category: category || 'general',
        p_target_role: targetRole || 'all',
        p_id: id || null,
      });
      if (error) throw error;
      return Array.isArray(data) ? data[0] : data;
    } catch (e) {
      console.error('upsertTemplate error:', e);
      return null;
    }
  }

  static async deleteTemplate(id) {
    try {
      const { error } = await supabase.rpc('notification_template_delete', { p_id: id });
      if (error) throw error;
      return true;
    } catch (e) {
      console.error('deleteTemplate error:', e);
      return false;
    }
  }

  static async getAiPrompt() {
    const { data, error } = await serviceRoleClient
      .from('notification_ai_settings')
      .select('prompt, updated_at')
      .eq('id', 'default')
      .maybeSingle();
    if (error) throw error;
    return data?.prompt || '';
  }

  static async saveAiPrompt(prompt, updatedBy) {
    const row = {
      id: 'default',
      prompt: String(prompt || '').trim(),
      updated_at: new Date().toISOString(),
    };
    if (updatedBy) row.updated_by = updatedBy;
    const { data, error } = await serviceRoleClient
      .from('notification_ai_settings')
      .upsert(row, { onConflict: 'id' })
      .select()
      .single();
    if (error) throw error;
    return data;
  }

  static async generateAiCopies({ audience = 'customer', count = 100, brief = '', prompt = '' } = {}) {
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/notification-ai-generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ audience, count, brief, prompt }),
      });
      const json = await res.json().catch(() => ({}));
      if (json?.success && Array.isArray(json.items) && json.items.length) {
        return json.items;
      }
      throw new Error(json?.error || 'AI generate nahi ho paya');
    } catch (e) {
      const { copiesFromBank } = await import('../data/roadservePushCopyBank');
      const fallback = copiesFromBank(audience, count);
      if (fallback.length) return fallback;
      throw e;
    }
  }

  static async listAiCampaigns() {
    const { data, error } = await serviceRoleClient
      .from('notification_ai_campaigns')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  }

  static async saveAiCampaign(payload) {
    const row = {
      name: payload.name || 'AI daily campaign',
      target_role: payload.targetRole || 'customer',
      category: payload.category || 'reminder',
      send_time_ist: payload.sendTimeIst || '10:00',
      enabled: payload.enabled !== false,
      items: payload.items || [],
      next_index: payload.nextIndex || 0,
      updated_at: new Date().toISOString(),
    };
    if (payload.createdBy) row.created_by = payload.createdBy;
    if (payload.id) {
      const { data, error } = await serviceRoleClient
        .from('notification_ai_campaigns')
        .update(row)
        .eq('id', payload.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    }
    const { data, error } = await serviceRoleClient
      .from('notification_ai_campaigns')
      .insert(row)
      .select()
      .single();
    if (error) throw error;
    return data;
  }

  static async setAiCampaignEnabled(id, enabled) {
    const { data, error } = await serviceRoleClient
      .from('notification_ai_campaigns')
      .update({ enabled, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  }

  static async deleteAiCampaign(id) {
    const { error } = await serviceRoleClient
      .from('notification_ai_campaigns')
      .delete()
      .eq('id', id);
    if (error) throw error;
    return true;
  }

  static async runAiCampaignNow(campaignId) {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/notification-ai-daily-cron`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ campaign_id: campaignId, force: true }),
    });
    const json = await res.json().catch(() => ({}));
    if (!json?.success) {
      throw new Error(json?.error || 'Scheduler run fail hua');
    }
    return json;
  }
}
